import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function TypewriterText({ text, onComplete }: { text: string, onComplete?: () => void }) {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    let current = '';
    let i = 0;
    const interval = setInterval(() => {
      current += text[i];
      setDisplayed(current);
      i++;
      if (i >= text.length) {
        clearInterval(interval);
        onComplete?.();
      }
    }, 40);
    return () => clearInterval(interval);
  }, [text, onComplete]);

  return <span>{displayed}</span>;
}

export function InlineDropdown({ value, options, onChange, isOpen, onToggle }: { value: string, options: string[], onChange: (val: string) => void, isOpen: boolean, onToggle: () => void }) {
  return (
    <span className="relative inline-block mt-4 md:mt-0 mx-2">
      <button
        onClick={onToggle}
        className={`text-white border-b ${isOpen ? 'border-brand' : 'border-white/20'} px-2 font-serif italic hover:border-brand transition-colors inline-flex items-center gap-2`}
      >
        {value} <span className="text-[10px] opacity-50 block mb-1">▼</span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={onToggle}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:absolute md:top-full md:left-1/2 md:-translate-y-0 md:-translate-x-1/2 mt-0 md:mt-4 w-[85vw] max-w-[300px] md:max-w-none md:min-w-[280px] bg-[#0a0a0a] border border-brand/30 rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden"
          >
            {options.map(opt => (
              <button
                key={opt}
                onClick={() => { onChange(opt); }}
                className="px-4 md:px-6 py-4 text-base md:text-xl text-neutral-400 hover:text-white hover:bg-brand/10 transition-colors font-serif whitespace-normal md:whitespace-nowrap text-center border-b border-white/5 last:border-0"
              >
                {opt}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}

export function DynamicInput({ value, onChange, onKeyDown, placeholder, autoFocus, active, showSubmit, onSubmit, type = "text" }: { value: string, onChange: (e: any) => void, onKeyDown: (e: any) => void, placeholder: string, autoFocus: boolean, active: boolean, showSubmit: boolean, onSubmit: () => void, type?: string }) {
  return (
    <span className="relative inline-block mt-4 md:mt-0 mx-1 align-bottom max-w-full">
      {/* Invisible span for exact width measurement */}
      <span className="invisible px-1 font-serif italic whitespace-pre inline-block text-center" aria-hidden="true">
        {value || placeholder}
      </span>
      <input
        autoFocus={autoFocus}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        className={`absolute left-0 bottom-0 w-full h-full bg-transparent border-b ${active ? 'border-brand' : 'border-white/20'} hover:border-brand/50 focus:outline-none focus:border-brand transition-colors px-1 text-center font-serif italic text-white placeholder:text-neutral-700/50 rounded-none overflow-hidden pb-1`}
      />
      {showSubmit && (
        <button onClick={onSubmit} className="absolute -right-6 top-1/2 -translate-y-1/2 text-xs text-brand font-sans uppercase animate-pulse hover:text-white md:cursor-none">
          ↵
        </button>
      )}
    </span>
  );
}

export function ConversationalContact() {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [topic, setTopic] = useState('eine Brand Collaboration');
  const [email, setEmail] = useState('');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const topicOptions = [
    'eine Brand Collaboration',
    'ein Fashion Editorial',
    'ein Tech / AI Projekt',
    'ein Exklusiv-Interview'
  ];

  const nextStep = (targetStep: number) => {
    if (step < targetStep) setStep(targetStep);
  };

  return (
    <div className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl text-neutral-400 max-w-4xl mx-auto md:mx-0 leading-[1.8] md:leading-[2.5] text-center md:text-left relative pb-16">
      {step === 0 && (
        <div className="flex justify-center md:justify-start mt-8 md:mt-0">
          <button onClick={() => nextStep(1)} className="px-8 py-4 bg-white/5 border border-white/10 text-white hover:bg-brand hover:border-brand hover:text-black transition-all duration-500 uppercase tracking-widest text-xs font-sans md:cursor-none">
            Nachricht verfassen
          </button>
        </div>
      )}

      {step >= 1 && (
        <div className="relative mt-8 md:mt-0 break-words">
          {step === 1 && <TypewriterText text="Hallo Emilia. Ich bin " onComplete={() => nextStep(2)} />}
          {step >= 2 && <span>Hallo Emilia. Ich bin </span>}

          {step >= 2 && (
            <DynamicInput
              autoFocus={step === 2}
              value={name}
              onChange={(e: any) => setName(e.target.value)}
              onKeyDown={(e: any) => e.key === 'Enter' && name && nextStep(3)}
              placeholder="Dein Name"
              active={step === 2}
              showSubmit={step === 2 && name.length > 0}
              onSubmit={() => name && nextStep(3)}
            />
          )}
          {step >= 2 && <span>. </span>}

          {step === 3 && <TypewriterText text="Ich vertrete " onComplete={() => nextStep(4)} />}
          {step >= 4 && <span>Ich vertrete </span>}

          {step >= 4 && (
            <span className="inline-block relative">
              <DynamicInput
                autoFocus={step === 4}
                value={company}
                onChange={(e: any) => setCompany(e.target.value)}
                onKeyDown={(e: any) => e.key === 'Enter' && company && nextStep(5)}
                placeholder="Firma / Brand"
                active={step === 4}
                showSubmit={step === 4 && company.length > 0}
                onSubmit={() => company && nextStep(5)}
              />
            </span>
          )}
          {step >= 4 && <span>. </span>}

          {step === 5 && <TypewriterText text="Wir würden gerne über " onComplete={() => nextStep(6)} />}
          {step >= 6 && <span>Wir würden gerne über </span>}

          {step >= 6 && (
            <span className="inline-block relative">
              <InlineDropdown
                value={topic}
                options={topicOptions}
                isOpen={isDropdownOpen}
                onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
                onChange={(val) => {
                  setTopic(val);
                  setIsDropdownOpen(false);
                  if (step === 6) nextStep(7);
                }}
              />
              {step === 6 && !isDropdownOpen && <button onClick={() => nextStep(7)} className="absolute -right-6 md:-right-4 top-1/2 -translate-y-1/2 text-xs text-brand font-sans uppercase animate-pulse hover:text-white md:cursor-none">↵</button>}
            </span>
          )}
          {step >= 6 && <span> sprechen. </span>}

          {step === 7 && <TypewriterText text="Meine E-Mail ist " onComplete={() => nextStep(8)} />}
          {step >= 8 && <span className="mt-4 md:mt-0">Meine E-Mail ist </span>}

          {step >= 8 && (
            <span className="inline-block relative pb-16 md:pb-0">
              <DynamicInput
                autoFocus={step === 8}
                type="email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                onKeyDown={(e: any) => e.key === 'Enter' && email && nextStep(9)}
                placeholder="email@domain.com"
                active={step === 8}
                showSubmit={step === 8 && email.length > 0}
                onSubmit={() => email && nextStep(9)}
              />
            </span>
          )}
          {step >= 8 && <span>.</span>}
        </div>
      )}

      {step >= 9 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-16 flex justify-center md:justify-start">
          <button onClick={() => setStep(10)} disabled={step === 10} className="px-8 py-4 bg-white/5 border border-white/10 text-white hover:bg-brand hover:border-brand hover:text-black transition-all duration-500 uppercase tracking-widest text-xs font-sans disabled:opacity-50 disabled:border-brand disabled:text-brand md:cursor-none w-full md:w-auto">
            {step === 9 ? 'Signal Senden' : 'Protocol Complete ✓'}
          </button>
        </motion.div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                7. TERMINAL EASTER EGG                      */
/* -------------------------------------------------------------------------- */
